<script setup lang="ts">
import {CourseDiscuss} from "@/types";
import {TimeFormatter} from "@/utils/common.ts";
import DiscussReplyCard from "components/course/discuss/DiscussReplyCard.vue";
import {nextTick, reactive, ref} from "vue";
import {CourseService} from "@/api/api.ts";
import {ElMessageBox, ElNotification} from "element-plus";
import DiscussReplyDialog from "components/course/discuss/DiscussReplyDialog.vue";

const discussReplyDialog = ref<InstanceType<typeof DiscussReplyDialog>>()
let currentReplyId = 0

const props = defineProps<{
  courseDiscuss: CourseDiscuss
}>()

const loading = ref<boolean>(false);
const pagination = reactive({
  page: 1,
  pageSize: 5,
  total: 0,
})

const discussReplyCard = ref<InstanceType<typeof DiscussReplyCard>>()
const emit = defineEmits(["onReply", "onExpand", "onDel"])
const expanded = ref(false)

const onReply = () => {
  emit("onReply", props.courseDiscuss)
  expanded.value = false
}

const onDel = () => {
  emit("onDel", props.courseDiscuss)
  expanded.value = false
}

const onReplyItem = (id: number) => {
  currentReplyId = id
  discussReplyDialog.value?.show()
}

const onDelItem = async (id: number) => {
  currentReplyId = id
  try {
    await ElMessageBox.confirm(
        '确认删除该讨论内容吗',
        '注意',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
    )
    const res = await CourseService.delDiscussReply({
      id: id,
    })
    if (res.data.code === 200) {
      ElNotification.success({
        title: '删除成功',
        message: res.data.msg,
        type: 'error'
      })
      props.courseDiscuss.comment_num--
      onCollapse()
    } else {
      ElNotification.error({
        title: '删除失败',
        message: res.data.msg,
        type: 'error'
      })
    }
  } catch (error) {
    console.warn(error)
  }
}

const onSubmit = async (content: string) => {
  if (!content) {
    return ElNotification.warning({
      title: "注意",
      message: "回复内容不能为空",
      type: "error",
    })
  }
  try {
    const res =  await CourseService.replyDiscuss({
      id: currentReplyId,
      content: content,
    })
    if (res.data.code === 200) {
      ElNotification.success({
        title: "回复成功",
        message: res.data.msg,
        type: "success",
      })
      discussReplyDialog.value?.hide()
      props.courseDiscuss.comment_num++
      await onExpand()
    } else {
      ElNotification.error({
        title: "回复失败",
        message: res.data.msg,
        type: "error",
      })
    }
  } catch (error) {
    console.log(error)
  }
}

const onExpand = async() => {
  expanded.value = true
  pagination.page = 1
  pagination.pageSize = 5
  await nextTick()
  await getDiscussReply()
}

const getDiscussReply = async () => {
  try {
    loading.value = true
    const res = await CourseService.getDiscussReplyInfo({
      page: pagination.page,
      pageSize: pagination.pageSize,
      id: props.courseDiscuss.id
    })
    if (res.data.code === 200) {
      pagination.total = res.data.data?.count || 0
      pagination.pageSize = res.data.data?.pageSize || 5
      pagination.page = res.data.data?.page || 1
      props.courseDiscuss.replyInfo = res.data.data
    } else {
      ElNotification.error({
        title: "获取详情失败",
        message: res.data.msg,
        type: "error",
      })
    }
  } catch (err) {
    console.log(err)
  } finally {
    loading.value = false
  }
}

const onCollapse = () => {
  expanded.value = false
}

const onPaginationChange = (page: number, pageSize: number) => {
  pagination.page = page
  pagination.pageSize = pageSize
  getDiscussReply()
}

const showWarning = () => {
  ElNotification.info({
    message: '暂不支持PC端点赞,请使用移动端~',
  })
}
</script>

<template>
  <div class="mx-2rem">
    <div class="flex gap-16px mt-1rem">
      <el-avatar :size="32" :src="courseDiscuss.head_img"/>
      <div class="flex-1">
        <div class="text-0.875rem text-[rgba(0,0,0,0.651)] font-bold">
          {{ courseDiscuss.user_name }}
        </div>
        <div class="text-0.875rem text-[rgba(0,0,0,0.651)] mt-.75rem">
          {{ courseDiscuss.content }}
        </div>
        <div class="mt-1rem">
          <el-image
              style="width: 7.5rem; height: 7.5rem"
              :src="item"
              :zoom-rate="1.2"
              :max-scale="7"
              :min-scale="0.2"
              :preview-src-list="courseDiscuss.images"
              :initial-index="index"
              fit="cover"
              class="mr-1rem rounded"
              v-for="(item, index) in courseDiscuss.images"
          />
        </div>
      </div>
      <div class="flex flex-col items-end">
        <el-image :src="courseDiscuss.thumb_image" class="w-8.75rem h-5rem rounded"></el-image>
        <div class="mt-.75rem text-[rgba(0,0,0,0.251)] text-.875rem">{{ `来自：${courseDiscuss.title}` }}</div>
      </div>
    </div>
    <div class="ml-48px mt-.75rem flex">
      <div class="text-.85em text-[rgba(0,0,0,0.251)] mr-4rem">
        {{ TimeFormatter.formatTime(courseDiscuss.create_time * 1000) }}
      </div>
      <div class="flex gap-.25rem mr-2rem items-center cursor-pointer" @click="showWarning">
        <img src="@/assets/image/icon/icon-praise.png" class="w-1rem h-1rem" alt="icon-praise"/>
        <div class="text-.75em text-[rgba(0,0,0,0.251)] mt-2px">{{ courseDiscuss.like_num }}</div>
      </div>
      <div class="flex gap-.25rem mr-2rem items-center cursor-pointer" @click="onReply">
        <img src="@/assets/image/icon/icon-reply.png" class="w-1rem h-1rem" alt="icon-reply"/>
        <div class="text-.75em text-[rgba(0,0,0,0.251)] mt-2px">回复</div>
      </div>
      <div class="flex gap-.25rem items-center cursor-pointer" @click="onDel">
        <img src="@/assets/image/icon/icon-delete.png" class="w-1rem h-1rem" alt="icon-del"/>
        <div class="text-.75em text-[rgba(0,0,0,0.251)] mt-2px">删除</div>
      </div>
    </div>
    <div class="flex items-center ml-48px mt-1rem cursor-pointer" @click="onExpand"
         v-if="courseDiscuss.comment_num && !expanded">
      <div class="text-.75em text-[rgba(0,0,0,0.251)] mr-.75rem">
        展开{{ courseDiscuss.comment_num }}条回复
      </div>
      <img src="@/assets/image/icon/icon-expand.png" alt="icon-expand" class="w-.75rem"/>
    </div>
    <discuss-reply-card
        :pagination="pagination"
        ref="discussReplyCard"
        class="mt-1rem animate-fade-in"
        v-if="expanded"
        :reply-info="courseDiscuss.replyInfo"
        @collapse="onCollapse"
        @on-reply="onReplyItem"
        @on-delete="onDelItem"
        @on-pagination-change="onPaginationChange"
        v-loading="loading"/>
    <div class="bg-#e9e9e9 h-1px mt-1rem"></div>
  </div>
  <discuss-reply-dialog ref="discussReplyDialog" @on-submit="onSubmit" />
</template>

<style scoped>
</style>
